import { TaskTracking } from '@mimir/common/async/taskTracking';
import type { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { makeError } from '@mimir/common/util/exception';
import type { AudioTransform } from './audioTransform';
import type { FragmentedMp4Buffers } from '@mimir/common/mp4/fragmentedMp4Types';
import type { Cleanable } from '@mimir/common/util/cleanable';

function fetchUrlAsBuffer(url: string): Promise<ArrayBuffer> {
  const xhr = new XMLHttpRequest();
  xhr.open('get', url);
  xhr.responseType = 'arraybuffer';
  return new Promise<ArrayBuffer>((resolve, reject) => {
    xhr.onload = () => {
      if (xhr.status >= 300) {
        reject(
          makeError(xhr.status + ' ' + xhr.statusText, 'HttpBufferFetchError', {
            info: { code: '' + xhr.status, url }
          })
        );
      }
      const contentType = xhr.getResponseHeader('content-type');
      if (contentType && contentType.startsWith('text')) {
        reject(
          makeError(
            'Unexpected ' + contentType + ' response',
            'HttpBufferFetchError',
            { info: { contentType, url } }
          )
        );
      }
      resolve(xhr.response as ArrayBuffer);
    };
    xhr.onerror = reject;
    xhr.send();
  });
}

/**
 * Fetch buffers using HTTP GET, while keeping track of what is being fetched,
 * to keep control over concurrency and to be able to piggyback on ongoing HTTP
 * requests.
 */
export class BufferFetching {
  readonly tracking : TaskTracking<ArrayBuffer>;
  constructor(completionHandler?: (concurrency: number) => void) {
    this.tracking = new TaskTracking<ArrayBuffer>(completionHandler);
  }
  async getBuffer(url: string): Promise<ArrayBuffer> {
    return this.tracking.runOrPiggyback(url, () => fetchUrlAsBuffer(url));
  }
}

export interface BufferingContext {
  fragmentedMp4Provider: FragmentedMp4Provider;
  currentTime: number;
}

export interface FragmentedMp4Provider extends Cleanable {
  readonly itemId: string;

  getMediaSegmentBuffers(needInitialization: boolean, streamId: StreamRoleId, segmentTime: number, audioTransform: AudioTransform): Promise<FragmentedMp4Buffers>;
}
